'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@planner/ui'
import { Button } from '@planner/ui'
import { Badge } from '@planner/ui'
import type { MenuPackage, MenuPackageCategory } from '@planner/types'
import { Check } from 'lucide-react'
import { pluralizeDish } from '@/lib/utils'

interface PackageCategoryInfo {
  category: MenuPackageCategory & { menu_category?: { name: string } }
  itemsCount: number
}

interface PackagePreviewCardProps {
  package: MenuPackage
  categories: PackageCategoryInfo[]
  isSelected: boolean
  onSelect: (packageId: string) => void
}

export function PackagePreviewCard({
  package: pkg,
  categories,
  isSelected,
  onSelect,
}: PackagePreviewCardProps) {
  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-lg ${
        isSelected
          ? 'border-primary bg-primary/5 ring-2 ring-primary'
          : 'hover:border-primary/50'
      }`}
      onClick={() => onSelect(pkg.id)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{pkg.name}</CardTitle>
            {pkg.description && (
              <p className="text-sm text-muted-foreground mt-1">{pkg.description}</p>
            )}
          </div>
          {isSelected && (
            <Badge variant="default" className="ml-2">
              <Check className="w-3 h-3 mr-1" />
              Wybrany
            </Badge>
          )}
        </div>
        <div className="mt-2">
          <span className="text-2xl font-bold">{pkg.price_per_person.toFixed(2)}</span>
          <span className="text-sm text-muted-foreground ml-1">zł/os</span>
        </div>
      </CardHeader>
      <CardContent>
        {categories.length > 0 ? (
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">Kategorie:</div>
            <div className="space-y-2">
              {categories.map((catInfo) => {
                const categoryName = catInfo.category.menu_category?.name || 'Kategoria'
                return (
                  <div key={catInfo.category.id} className="text-sm">
                    <div className="flex items-center">
                      <span className="font-medium">{categoryName}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Wybierz {catInfo.category.max_selections} z {catInfo.itemsCount} {pluralizeDish(catInfo.itemsCount)}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Brak kategorii w pakiecie</div>
        )}
        <div className="mt-4 pt-4 border-t">
          <Button
            variant={isSelected ? 'default' : 'outline'}
            className="w-full"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(pkg.id)
            }}
          >
            {isSelected ? 'Kontynuuj konfigurację' : 'Wybierz pakiet'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

